import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
} from "@mui/material";
import ContactSection from "./ContactSection";

interface Contact {
  name: string;
  email: string;
  phone: string;
}

interface Company {
  hrContacts: Contact[];
  financeContacts: Contact[];
  payrollContacts: Contact[];
}

interface EditContactsModalProps {
  open: boolean;
  onClose: () => void;
  company: Company;
  onSave: (updatedContacts: {
    hrContacts: Contact[];
    financeContacts: Contact[];
    payrollContacts: Contact[];
  }) => Promise<void>;
}

const EditContactsModal: React.FC<EditContactsModalProps> = ({
  open,
  onClose,
  company,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    hrContacts: company.hrContacts || [{ name: "", email: "", phone: "" }],
    financeContacts: company.financeContacts || [{ name: "", email: "", phone: "" }],
    payrollContacts: company.payrollContacts || [{ name: "", email: "", phone: "" }],
  });

  const [formErrors, setFormErrors] = useState({
    hrContacts: [] as Array<{ name?: string; email?: string; phone?: string }>,
    financeContacts: [] as Array<{ name?: string; email?: string; phone?: string }>,
    payrollContacts: [] as Array<{ name?: string; email?: string; phone?: string }>,
  });

  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string>("");

  useEffect(() => {
    if (open) {
      // Initialize form data when modal opens
      setFormData({
        hrContacts: company.hrContacts?.length > 0
          ? company.hrContacts
          : [{ name: "", email: "", phone: "" }],
        financeContacts: company.financeContacts?.length > 0
          ? company.financeContacts
          : [{ name: "", email: "", phone: "" }],
        payrollContacts: company.payrollContacts?.length > 0
          ? company.payrollContacts
          : [{ name: "", email: "", phone: "" }],
      });
      // Initialize errors array
      setFormErrors({
        hrContacts: (company.hrContacts || [{ name: "", email: "", phone: "" }]).map(() => ({})),
        financeContacts: (company.financeContacts || [{ name: "", email: "", phone: "" }]).map(() => ({})),
        payrollContacts: (company.payrollContacts || [{ name: "", email: "", phone: "" }]).map(() => ({})),
      });
      setValidationError("");
    }
  }, [open, company]);

  const validateContacts = (): boolean => {
    const errors = {
      hrContacts: [] as Array<{ name?: string; email?: string; phone?: string }>,
      financeContacts: [] as Array<{ name?: string; email?: string; phone?: string }>,
      payrollContacts: [] as Array<{ name?: string; email?: string; phone?: string }>,
    };

    let isValid = true;

    // Validate HR contacts
    const validHrContacts = formData.hrContacts.filter(
      (c) => c.name.trim() || c.email.trim() || c.phone.trim()
    );
    if (validHrContacts.length === 0) {
      setValidationError("At least one HR contact is required");
      isValid = false;
    } else {
      formData.hrContacts.forEach((contact, index) => {
        const contactErrors: { name?: string; email?: string; phone?: string } = {};
        if (contact.name.trim() && !contact.email.trim()) {
          contactErrors.email = "Email is required when name is provided";
          isValid = false;
        }
        if (contact.email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(contact.email)) {
            contactErrors.email = "Invalid email format";
            isValid = false;
          }
        }
        errors.hrContacts[index] = contactErrors;
      });
    }

    // Validate Finance contacts
    const validFinanceContacts = formData.financeContacts.filter(
      (c) => c.name.trim() || c.email.trim() || c.phone.trim()
    );
    if (validFinanceContacts.length === 0) {
      setValidationError("At least one Finance contact is required");
      isValid = false;
    } else {
      formData.financeContacts.forEach((contact, index) => {
        const contactErrors: { name?: string; email?: string; phone?: string } = {};
        if (contact.name.trim() && !contact.email.trim()) {
          contactErrors.email = "Email is required when name is provided";
          isValid = false;
        }
        if (contact.email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(contact.email)) {
            contactErrors.email = "Invalid email format";
            isValid = false;
          }
        }
        errors.financeContacts[index] = contactErrors;
      });
    }

    // Validate Payroll contacts
    const validPayrollContacts = formData.payrollContacts.filter(
      (c) => c.name.trim() || c.email.trim() || c.phone.trim()
    );
    if (validPayrollContacts.length === 0) {
      setValidationError("At least one Payroll contact is required");
      isValid = false;
    } else {
      formData.payrollContacts.forEach((contact, index) => {
        const contactErrors: { name?: string; email?: string; phone?: string } = {};
        if (contact.name.trim() && !contact.email.trim()) {
          contactErrors.email = "Email is required when name is provided";
          isValid = false;
        }
        if (contact.email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(contact.email)) {
            contactErrors.email = "Invalid email format";
            isValid = false;
          }
        }
        errors.payrollContacts[index] = contactErrors;
      });
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    setValidationError("");

    if (!validateContacts()) {
      return;
    }

    setSaving(true);
    try {
      // Filter out empty contacts
      const cleanedData = {
        hrContacts: formData.hrContacts.filter(
          (c) => c.name.trim() || c.email.trim() || c.phone.trim()
        ),
        financeContacts: formData.financeContacts.filter(
          (c) => c.name.trim() || c.email.trim() || c.phone.trim()
        ),
        payrollContacts: formData.payrollContacts.filter(
          (c) => c.name.trim() || c.email.trim() || c.phone.trim()
        ),
      };

      await onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error("Error saving contacts:", error);
      setValidationError("Failed to save contacts. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Company Contacts</DialogTitle>
      <DialogContent>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <ContactSection
          title="HR Contact Details"
          description="Key contact(s) that will review and approve employee applications"
          contacts={formData.hrContacts}
          errors={formErrors.hrContacts}
          onContactChange={(index, field, value) => {
            const updatedContacts = [...formData.hrContacts];
            updatedContacts[index] = { ...updatedContacts[index], [field]: value };
            setFormData({ ...formData, hrContacts: updatedContacts });
            // Clear validation error when user starts editing
            if (validationError) setValidationError("");
          }}
          onAddContact={() => {
            setFormData({
              ...formData,
              hrContacts: [...formData.hrContacts, { name: "", email: "", phone: "" }],
            });
            setFormErrors({
              ...formErrors,
              hrContacts: [...formErrors.hrContacts, {}],
            });
          }}
          onRemoveContact={(index) => {
            setFormData({
              ...formData,
              hrContacts: formData.hrContacts.filter((_, i) => i !== index),
            });
            setFormErrors({
              ...formErrors,
              hrContacts: formErrors.hrContacts.filter((_, i) => i !== index),
            });
          }}
          submitting={saving}
          sectionKey="hr"
          availableContacts={{
            finance: formData.financeContacts,
            payroll: formData.payrollContacts,
          }}
          onCopyFrom={(contact) => {
            if (formData.hrContacts.length === 1 &&
              !formData.hrContacts[0].name &&
              !formData.hrContacts[0].email &&
              !formData.hrContacts[0].phone) {
              setFormData({ ...formData, hrContacts: [{ ...contact }] });
            } else {
              setFormData({ ...formData, hrContacts: [...formData.hrContacts, { ...contact }] });
            }
          }}
        />

        <ContactSection
          title="Finance Contact Details"
          description="Key contact(s) that will handle invoicing related queries"
          contacts={formData.financeContacts}
          errors={formErrors.financeContacts}
          onContactChange={(index, field, value) => {
            const updatedContacts = [...formData.financeContacts];
            updatedContacts[index] = { ...updatedContacts[index], [field]: value };
            setFormData({ ...formData, financeContacts: updatedContacts });
            if (validationError) setValidationError("");
          }}
          onAddContact={() => {
            setFormData({
              ...formData,
              financeContacts: [...formData.financeContacts, { name: "", email: "", phone: "" }],
            });
            setFormErrors({
              ...formErrors,
              financeContacts: [...formErrors.financeContacts, {}],
            });
          }}
          onRemoveContact={(index) => {
            setFormData({
              ...formData,
              financeContacts: formData.financeContacts.filter((_, i) => i !== index),
            });
            setFormErrors({
              ...formErrors,
              financeContacts: formErrors.financeContacts.filter((_, i) => i !== index),
            });
          }}
          submitting={saving}
          sectionKey="finance"
          availableContacts={{
            hr: formData.hrContacts,
            payroll: formData.payrollContacts,
          }}
          onCopyFrom={(contact) => {
            if (formData.financeContacts.length === 1 &&
              !formData.financeContacts[0].name &&
              !formData.financeContacts[0].email &&
              !formData.financeContacts[0].phone) {
              setFormData({ ...formData, financeContacts: [{ ...contact }] });
            } else {
              setFormData({ ...formData, financeContacts: [...formData.financeContacts, { ...contact }] });
            }
          }}
        />

        <ContactSection
          title="Payroll Contact Details"
          description="Key contact(s) that will handle payroll and salary sacrifice related queries"
          contacts={formData.payrollContacts}
          errors={formErrors.payrollContacts}
          onContactChange={(index, field, value) => {
            const updatedContacts = [...formData.payrollContacts];
            updatedContacts[index] = { ...updatedContacts[index], [field]: value };
            setFormData({ ...formData, payrollContacts: updatedContacts });
            if (validationError) setValidationError("");
          }}
          onAddContact={() => {
            setFormData({
              ...formData,
              payrollContacts: [...formData.payrollContacts, { name: "", email: "", phone: "" }],
            });
            setFormErrors({
              ...formErrors,
              payrollContacts: [...formErrors.payrollContacts, {}],
            });
          }}
          onRemoveContact={(index) => {
            setFormData({
              ...formData,
              payrollContacts: formData.payrollContacts.filter((_, i) => i !== index),
            });
            setFormErrors({
              ...formErrors,
              payrollContacts: formErrors.payrollContacts.filter((_, i) => i !== index),
            });
          }}
          submitting={saving}
          sectionKey="payroll"
          availableContacts={{
            hr: formData.hrContacts,
            finance: formData.financeContacts,
          }}
          onCopyFrom={(contact) => {
            if (formData.payrollContacts.length === 1 &&
              !formData.payrollContacts[0].name &&
              !formData.payrollContacts[0].email &&
              !formData.payrollContacts[0].phone) {
              setFormData({ ...formData, payrollContacts: [{ ...contact }] });
            } else {
              setFormData({ ...formData, payrollContacts: [...formData.payrollContacts, { ...contact }] });
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditContactsModal;


